"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageEcommerceSchema } from "@/sanity/pages/PageEcommerceTemplate";

export default function VsTableSection({
  data,
}: {
  data: PageEcommerceSchema;
}) {
  if (!data.vsTitle && !data.vsRows?.length) return null;

  const rows = data.vsRows ?? [];
  const last = rows.length - 1;

  return (
    <section className="w-full bg-white pt-[12rem] lg:pt-[18rem]">
      <div className="mx-auto w-[min(140rem,100%)] flex flex-col items-center gap-[6rem] lg:gap-[8rem]">
        {/* Header */}
        <div className="flex flex-col items-center gap-[2rem] text-center max-w-[96rem] px-[3rem]">
          {data.vsChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.vsChip}
            </p>
          )}
          {data.vsTitle && (
            <div className="richtext richtext-center prose-headings:font-medium prose-headings:text-[5rem] lg:prose-headings:text-[6rem] prose-headings:leading-[1.15] prose-headings:tracking-[-0.3rem] prose-headings:text-black prose-em:text-[#003828] prose-em:not-italic prose-em:font-blacker">
              <PortableText
                value={data.vsTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto scrollbar-hide px-[3rem]">
          <div className="min-w-[90rem] flex items-end">
            {/* Label column — no header, data rows only */}
            <div className="flex flex-col shrink-0 lg:min-w-[37rem]">
              {rows.map((row, i) => (
                <div
                  key={row._key ?? i}
                  className={`flex items-center min-h-[8.5rem] px-[2.5rem] py-[1.75rem] border-black/10
                    ${i === 0 ? "border rounded-tl-[1rem]" : "border-b border-l border-r"}
                    ${i === last ? "rounded-bl-[1rem]" : ""}
                  `}
                >
                  <span className="font-geist font-medium text-[2.125rem] text-black tracking-[-0.085rem] leading-[1.5]">
                    {row.label}
                  </span>
                </div>
              ))}
            </div>

            {/* IQ Capital column */}
            <div className="flex flex-col flex-1">
              <div className="flex items-center justify-center min-h-[8.5rem] px-[2.5rem] py-[1.75rem] bg-[#1aa87f] rounded-tl-[1rem]">
                <span className="font-geist font-bold text-[2.75rem] text-white tracking-[-0.11rem] leading-[1.5]">
                  {data.vsIqLabel ?? "IQ Capital"}
                </span>
              </div>
              {rows.map((row, i) => (
                <div
                  key={row._key ?? i}
                  className="flex items-center justify-center min-h-[8.5rem] px-[2.5rem] py-[1.75rem] bg-[rgba(26,168,127,0.1)] border-b border-r border-black/10"
                >
                  <span className="font-geist font-semibold text-[2.125rem] text-[#001b14] tracking-[-0.085rem] leading-[1.5]">
                    {row.iqValue}
                  </span>
                </div>
              ))}
            </div>

            {/* Competitor column */}
            <div className="flex flex-col flex-1">
              <div className="flex items-center justify-center min-h-[8.5rem] px-[2.5rem] py-[1.75rem] border border-black/10 rounded-tr-[1rem]">
                <span className="font-geist font-medium text-[2.25rem] text-black/90 tracking-[-0.09rem] leading-[1.5]">
                  {data.vsCompetitorLabel ?? "Typische Prop-Firma"}
                </span>
              </div>
              {rows.map((row, i) => (
                <div
                  key={row._key ?? i}
                  className={`flex items-center justify-center min-h-[8.5rem] px-[2.5rem] py-[1.75rem] border-b border-r border-black/10 ${i === last ? "rounded-br-[1rem]" : ""}`}
                >
                  <span className="font-geist font-normal text-[2.125rem] text-black/60 tracking-[-0.085rem] leading-[1.5]">
                    {row.competitorValue}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
